import React, { useEffect, useState } from 'react';
import { IRegiftCard } from '../models/RegiftCard.model';
import IKeyValue from '../models/core/KeyValue.interface';
import { WorkflowTypeEnum } from '../models/enums/WorkflowTypeEnum';
import { IActivateCardSettings, IAppSettings, ICardSettings, IElavonSettings, IReCaptchaSettings, ITotalProcessingSettings, IWalletSettings } from "../models/interfaces/AppSettings.interface";
import ActivateGiftCardPage from '../pages/ActivateGiftCardPage';
import BuyGiftCardPageNew from '../pages/BuyGiftCardPageNew';
import RegiftCardPage from '../pages/RegiftCardPage';
import DataService from '../services/DataService.service';
import './Application.scss';
import LoadingComponent from "./shared/LoadingComponent";
//import _cloneDeep from 'lodash/cloneDeep';

// Global context of all app settings: culture, labels, general pages and links
export const AppSettingContext = React.createContext({} as IAppSettings);

// Global context of RegiftCard page
export const RegiftCardContext = React.createContext({} as IRegiftCard);

const Application = () => {
    const [appSettings, setAppSettings] = useState({} as IAppSettings);
    const [appWorkflowType, setAppWorkflowType] = useState(WorkflowTypeEnum.BUY_GIFT_CARD);
    const [isLoading, setIsLoading] = useState(true); //set to true to delay child components rendering

    // Check app variant based on global app element and its data attribute: data-app-step
    const appElement = document.getElementById('app');
    const appStep = appElement?.getAttribute("data-app-step"); // buy-gift, regift, activate-giftcard

    // Load app settings
    useEffect(() => {
        if (appStep.toLowerCase() === "regift") {
            setAppWorkflowType(WorkflowTypeEnum.REGIFT_CARD);
        } else if (appStep.toLowerCase() === "activate-giftcard") {
            setAppWorkflowType(WorkflowTypeEnum.ACTIVATE_GIFTCARD);
        }

        const culture = appElement?.getAttribute("data-culture");
        const settingsNodeId = appElement?.getAttribute("data-settings-node-id");

        // Initialize global AppContext  
        DataService.getAppSettings(culture, settingsNodeId)
            .then(result => {
                const _appSettings = {} as IAppSettings;
                _appSettings.debugMode = false;
                _appSettings.culture = culture;
                _appSettings.labels = new Map<string, string>();
                _appSettings.cardSettings = {} as ICardSettings;
                _appSettings.tpSettings = {} as ITotalProcessingSettings;
                _appSettings.elavonSettings = {} as IElavonSettings;
                _appSettings.activateSettings = {} as IActivateCardSettings;
                _appSettings.walletSettings = {} as IWalletSettings;
                _appSettings.reCaptchaSettings = {} as IReCaptchaSettings;

                if (result) {
                    _appSettings.debugMode = result.debugMode;
                    _appSettings.culture = result.culture;
                    _appSettings.links = result.links;
                    _appSettings.cardSettings = result.cardSettings;

                    _appSettings.tpSettings = result.tpSettings;
                    _appSettings.elavonSettings = result.elavonSettings;
                    _appSettings.activateSettings = result.activateSettings;
                    _appSettings.walletSettings = result.walletSettings;
                    _appSettings.reCaptchaSettings = result.reCaptchaSettings;

                    if (result.labels) {
                        result.labels.forEach((k: IKeyValue, i: number) => {
                            if (!_appSettings.labels.has(k.key)) {
                                _appSettings.labels.set(k.key, k.value);
                            }
                        });
                    }
                }

                setAppSettings(_appSettings);
            }).catch(error => {
                console.error(error);
            }).finally(() => {
                setIsLoading(false);
            });
    }, []);

    return (
        /* When running in React.StrictMode, all child components down the tree are rendered twice - simultaneously */
        /*<React.StrictMode>*/
        <>
            {isLoading && (
                <LoadingComponent large={true} />
            )}

            {(!isLoading && appSettings.labels.has("GiftCard.App.Steps.ChooseCard")) && (
                <AppSettingContext.Provider value={appSettings}>
                    {(appWorkflowType === WorkflowTypeEnum.BUY_GIFT_CARD) && (
                        /*<BuyGiftCardPage key={1} />*/
                        <BuyGiftCardPageNew key={1} />
                    )}

                    {appWorkflowType === WorkflowTypeEnum.REGIFT_CARD && (
                        <RegiftCardPage key={1} />
                    )}

                    {appWorkflowType === WorkflowTypeEnum.ACTIVATE_GIFTCARD && (
                        <ActivateGiftCardPage key={1} />
                    )}
                </AppSettingContext.Provider>
            )}
        </>
        /*</React.StrictMode>*/
    );
}

export default Application;